/**
 * Unified Correlation Detection Utilities
 * Single source of truth for parlay correlation logic
 *
 * Used by:
 * - useParlayBuilder (frontend) - blocks invalid additions
 * - parlayUtils (server) - warns about correlations in analysis
 * - BuildModePanel (frontend) - until deprecated
 */

import { GAME_MARKETS, type GameMarket } from './marketKey';
import { formatStatLabel } from './statLabels';

// =============================================================================
// Types
// =============================================================================

/**
 * Minimal leg interface for correlation detection
 * Accepts any leg-like object with these optional fields
 */
export interface CorrelationLeg {
  playerName?: string | null;
  player?: string | null; // Alternative field name
  team?: string | null;
  propType?: string | null;
  statType?: string | null; // Alternative field name
  overUnder?: string | null;
  line?: number | string | null;
  gameId?: string | null;
  sportsbook?: string | null;
}

/**
 * Result of checking if a leg can be added
 */
export interface BlockCheck {
  canAdd: boolean;
  blockReason?: string;
}

/**
 * Soft warning about correlation (doesn't block, just informs)
 */
export interface CorrelationWarning {
  type: 'SAME_PLAYER' | 'ALL_OVERS' | 'ALL_UNDERS' | 'SAME_GAME_SGP';
  message: string;
  severity: 'low' | 'medium';
  affectedLegs?: number[];
}

/**
 * Result of filtering blocked legs when adding multiple
 */
export interface FilterBlockedResult<T> {
  added: T[];
  blocked: { leg: T; reason: string }[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get player name from leg (handles both field names)
 */
function getPlayerName(leg: CorrelationLeg): string | null {
  return (leg.playerName ?? leg.player ?? null)?.toLowerCase()?.trim() ?? null;
}

/**
 * Get prop/stat type from leg (handles both field names)
 */
function getPropType(leg: CorrelationLeg): string {
  return (leg.propType ?? leg.statType ?? '').toLowerCase().trim();
}

/**
 * Check if a prop type is a team-level bet (not player prop)
 * Handles all game market variants: core (h2h/spreads/totals), period (h1/h2/q1-q4),
 * alternates, team totals, and AI statType values (spread/total/moneyline)
 */
export function isTeamBet(propType: string | undefined | null): boolean {
  if (!propType) return false;
  const lower = propType.toLowerCase();
  // Check full GAME_MARKETS array (includes team_totals, period markets, alternates)
  if (GAME_MARKETS.includes(lower as GameMarket)) return true;
  // Also check AI statType values (singular forms, their alternates, and moneyline)
  return [
    'spread',
    'total',
    'moneyline',
    'team_total',
    'alternate_spread',
    'alternate_total',
    'alternate_team_total',
  ].includes(lower);
}

/**
 * Get direction (over/under) normalized
 */
function getDirection(leg: CorrelationLeg): string | null {
  return leg.overUnder?.toLowerCase()?.trim() ?? null;
}

/** Single stat name -> canonical atom, covering every spelling the app emits. */
const STAT_ATOM: Record<string, string> = {
  points: 'points',
  pts: 'points',
  point: 'points',
  rebounds: 'rebounds',
  reb: 'rebounds',
  rebound: 'rebounds',
  assists: 'assists',
  ast: 'assists',
  assist: 'assists',
  blocks: 'blocks',
  blk: 'blocks',
  block: 'blocks',
  steals: 'steals',
  stl: 'steals',
  steal: 'steals',
  threes: 'threes',
  '3pm': 'threes',
  '3s': 'threes',
  three: 'threes',
  '3-pointer': 'threes',
  fg3m: 'threes',
};

/** Whole-string shorthand combo codes (e.g. "pra", "pr") to their component atoms. */
const SHORTHAND_COMBOS: Record<string, string[]> = {
  pra: ['points', 'rebounds', 'assists'],
  pr: ['points', 'rebounds'],
  pa: ['points', 'assists'],
  ra: ['rebounds', 'assists'],
  bs: ['blocks', 'steals'],
};

/**
 * Decompose a player-prop type into the atomic stats it covers, so overlap between a
 * combo and a component is detectable regardless of spelling. Handles every real
 * statType shape the app emits: `player_` prefixes, `_alternate` suffixes, long
 * (`points_rebounds_assists`), abbreviated (`pts_reb_ast`), `+`/space separators
 * (`Pts+Reb`), and shorthand codes (`pra`). Unknown props return `[]` (no overlap,
 * so an unrecognized prop is never over-blocked).
 */
export function playerStatComponents(propType: string): string[] {
  const cleaned = propType
    .toLowerCase()
    .trim()
    .replace(/^player_/, '')
    .replace(/_alternate$/, '');
  if (!cleaned) return [];
  if (SHORTHAND_COMBOS[cleaned]) return SHORTHAND_COMBOS[cleaned];
  const atoms = new Set<string>();
  for (const token of cleaned.split(/[\s_+]+/)) {
    const atom = STAT_ATOM[token];
    if (atom) atoms.add(atom);
  }
  return [...atoms];
}

// =============================================================================
// Sportsbook-Specific SGP Rules (Phase P1.5)
// =============================================================================

/**
 * Per-sportsbook SGP allowances: which same-game prop-type combos each book offers.
 * Display-only, consumed by SgpRulesNote to show the user what their book allows in a
 * same-game parlay. NOT enforced by isLegBlocked, which applies uniform conservative
 * blocking regardless of book (the builder has no same-game/SGP mode to switch on).
 *
 * true = offered in SGP by this book, false/absent = not offered.
 */
interface SgpRules {
  /** Allow same-player different prop types (e.g., LeBron pts Over + LeBron reb Over) */
  samePlayerDifferentProp: boolean;
  /** Allow player prop + same-team game prop (e.g., LeBron pts Over + LAL team total Over) */
  playerPropPlusTeamProp: boolean;
  /** Allow player prop + game total (e.g., LeBron pts Over + game total Over) */
  playerPropPlusGameTotal: boolean;
  /** Allow ML + game total in same game */
  mlPlusGameTotal: boolean;
}

export const SPORTSBOOK_SGP_RULES: Record<string, SgpRules> = {
  draftkings: {
    samePlayerDifferentProp: true,
    playerPropPlusTeamProp: true,
    playerPropPlusGameTotal: true,
    mlPlusGameTotal: true,
  },
  fanduel: {
    samePlayerDifferentProp: true,
    playerPropPlusTeamProp: true,
    playerPropPlusGameTotal: true,
    mlPlusGameTotal: false,
  },
  betmgm: {
    samePlayerDifferentProp: true,
    playerPropPlusTeamProp: false,
    playerPropPlusGameTotal: true,
    mlPlusGameTotal: true,
  },
  caesars: {
    samePlayerDifferentProp: true,
    playerPropPlusTeamProp: false,
    playerPropPlusGameTotal: false,
    mlPlusGameTotal: false,
  },
};

// =============================================================================
// Hard Block Detection (Sportsbook Rules)
// =============================================================================

/**
 * Check if adding a leg would be blocked due to correlation or sportsbook mismatch
 *
 * Hard blocks (sportsbook rules - these bets literally can't be combined):
 * - Same game totals (Over + Under on same game total)
 * - Same game spreads (any combination on same game)
 * - Same game moneyline (opposite teams)
 * - Cross-market: ML + Spread on same team in same game
 * - Same player + same prop type (any line/direction)
 * - Different sportsbook (can't mix books in same parlay)
 */
export function isLegBlocked(
  newLeg: CorrelationLeg,
  existingLegs: CorrelationLeg[],
  currentSportsbook: string | null,
): BlockCheck {
  // Check sportsbook mismatch first
  if (currentSportsbook && newLeg.sportsbook) {
    if (newLeg.sportsbook.toLowerCase() !== currentSportsbook.toLowerCase()) {
      return {
        canAdd: false,
        blockReason: `Parlay is locked to ${currentSportsbook}`,
      };
    }
  }

  const newPropType = getPropType(newLeg);
  const newIsTeamBet = isTeamBet(newPropType);
  const newPlayerName = getPlayerName(newLeg);
  const newTeam = newLeg.team?.toLowerCase()?.trim() ?? null;
  const newGameId = newLeg.gameId ?? null;

  for (const existingLeg of existingLegs) {
    const existingPropType = getPropType(existingLeg);
    const existingIsTeamBet = isTeamBet(existingPropType);
    const existingPlayerName = getPlayerName(existingLeg);
    const existingTeam = existingLeg.team?.toLowerCase()?.trim() ?? null;
    const existingGameId = existingLeg.gameId ?? null;

    // Team bets in same game
    if (newIsTeamBet && existingIsTeamBet && newGameId && existingGameId === newGameId) {
      // Same market type in same game (e.g., two spreads, two totals, two MLs)
      // Normalize prop types for comparison
      const normalizedNew =
        newPropType === 'spread'
          ? 'spreads'
          : newPropType === 'total'
            ? 'totals'
            : newPropType === 'moneyline'
              ? 'h2h'
              : newPropType;
      const normalizedExisting =
        existingPropType === 'spread'
          ? 'spreads'
          : existingPropType === 'total'
            ? 'totals'
            : existingPropType === 'moneyline'
              ? 'h2h'
              : existingPropType;

      if (normalizedNew === normalizedExisting) {
        return {
          canAdd: false,
          blockReason: `Same game ${formatStatLabel(normalizedNew)} already in parlay`,
        };
      }

      // Cross-market correlation: ML + Spread on same team
      const mlTypes = ['h2h', 'moneyline'];
      const spreadTypes = ['spreads', 'spread'];
      const isNewMl = mlTypes.includes(newPropType);
      const isNewSpread = spreadTypes.includes(newPropType);
      const isExistingMl = mlTypes.includes(existingPropType);
      const isExistingSpread = spreadTypes.includes(existingPropType);

      if ((isNewMl && isExistingSpread) || (isNewSpread && isExistingMl)) {
        // Block if same team (if team is covered, they also win outright)
        if (newTeam && existingTeam && newTeam === existingTeam) {
          return {
            canAdd: false,
            blockReason: 'ML and spread on same team are correlated',
          };
        }
        // If teams are undefined for both, be conservative and block
        if (!newTeam && !existingTeam) {
          return {
            canAdd: false,
            blockReason: 'ML and spread in same game may be correlated',
          };
        }
      }
    }

    // Player props: block same player + same or overlapping prop type
    if (!newIsTeamBet && !existingIsTeamBet) {
      if (newPlayerName && existingPlayerName && newPlayerName === existingPlayerName) {
        // Exact same prop type (identical spelling) keeps its specific message.
        // Equal covers two absent prop types too: same player, no prop info, block
        // conservatively rather than risk pairing two correlated legs blind.
        if (newPropType === existingPropType) {
          return {
            canAdd: false,
            blockReason: `Same player, same prop type (${formatStatLabel(newPropType)})`,
          };
        }

        // Block on any shared atomic stat, so a combo and one of its parts
        // (PRA + points), two combos sharing a stat (PRA + PR), or two spellings of
        // one stat (pts + points) all collapse to the same overlap. Priced as
        // independent they inflate the parlay's odds; the outcomes move together.
        const newComponents = playerStatComponents(newPropType);
        const existingComponents = playerStatComponents(existingPropType);
        const overlap = newComponents.filter((c) => existingComponents.includes(c));
        if (overlap.length > 0) {
          return {
            canAdd: false,
            blockReason: `Same player, overlapping stat (${overlap.map(formatStatLabel).join(', ')})`,
          };
        }
      }
    }
  }

  return { canAdd: true };
}

/**
 * Filter out blocked legs when adding multiple at once
 * Returns which legs were added and which were blocked with reasons
 */
export function filterBlockedLegs<T extends CorrelationLeg>(
  newLegs: T[],
  existingLegs: CorrelationLeg[],
  currentSportsbook: string | null,
): FilterBlockedResult<T> {
  const added: T[] = [];
  const blocked: { leg: T; reason: string }[] = [];

  // Build up the list of legs as we add them
  const allLegs = [...existingLegs];

  for (const leg of newLegs) {
    // Use the sportsbook from first leg if not set
    const sportsbook =
      currentSportsbook ?? (added.length > 0 ? (added[0].sportsbook ?? null) : null);

    const check = isLegBlocked(leg, allLegs, sportsbook);
    if (check.canAdd) {
      added.push(leg);
      allLegs.push(leg);
    } else {
      blocked.push({ leg, reason: check.blockReason ?? 'Blocked' });
    }
  }

  return { added, blocked };
}

// =============================================================================
// Soft Warning Detection (Informational)
// =============================================================================

/**
 * Detect correlation warnings for a parlay
 * These don't block but inform the user about correlated outcomes
 *
 * Soft warnings:
 * - Same player in multiple legs (different prop types)
 * - Same-game legs (odds shown are a cross-game estimate, the book prices the SGP lower)
 * - All overs (pace-dependent)
 * - All unders (pace-dependent)
 */
export function detectCorrelationWarnings(legs: CorrelationLeg[]): CorrelationWarning[] {
  const warnings: CorrelationWarning[] = [];

  if (legs.length < 2) return warnings;

  // Same player multiple props (different prop types - same type is blocked)
  const playerCounts: Record<string, number[]> = {};
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    const playerName = getPlayerName(leg);
    if (!playerName) continue;

    if (!playerCounts[playerName]) {
      playerCounts[playerName] = [];
    }
    playerCounts[playerName].push(i);
  }

  for (const [player, indices] of Object.entries(playerCounts)) {
    if (indices.length > 1) {
      // Capitalize first letter of each word for display
      const displayName = player
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

      warnings.push({
        type: 'SAME_PLAYER',
        message: `${displayName} appears in ${indices.length} legs - outcomes are correlated`,
        severity: 'medium',
        affectedLegs: indices,
      });
    }
  }

  // Same-game legs form a same-game parlay. The builder prices legs as an independent
  // product, but a book prices an SGP lower because the outcomes are correlated, so the
  // shown odds overstate the real payout. Warn once per game that has 2+ legs.
  const gameGroups: Record<string, number[]> = {};
  for (let i = 0; i < legs.length; i++) {
    const gameId = legs[i].gameId;
    if (!gameId) continue;
    gameGroups[gameId] ??= [];
    gameGroups[gameId].push(i);
  }
  for (const indices of Object.values(gameGroups)) {
    if (indices.length > 1) {
      warnings.push({
        type: 'SAME_GAME_SGP',
        message: `${indices.length} legs are in the same game. The odds shown are a cross-game estimate; your sportsbook will price this same-game parlay (SGP) lower.`,
        severity: 'medium',
        affectedLegs: indices,
      });
    }
  }

  // All overs (3+ legs, pace correlation)
  if (legs.length >= 3) {
    const directions = legs.map(getDirection);
    const allOvers = directions.every((d) => d === 'over');
    const allUnders = directions.every((d) => d === 'under');

    if (allOvers) {
      warnings.push({
        type: 'ALL_OVERS',
        message: 'All legs are OVER bets - correlated by game pace/tempo',
        severity: 'low',
      });
    }

    if (allUnders) {
      warnings.push({
        type: 'ALL_UNDERS',
        message: 'All legs are UNDER bets - correlated by game pace/tempo',
        severity: 'low',
      });
    }
  }

  return warnings;
}

// =============================================================================
// Convenience Functions
// =============================================================================

/**
 * Quick check if a leg would be blocked (without reason details)
 */
export function wouldBeBlocked(
  newLeg: CorrelationLeg,
  existingLegs: CorrelationLeg[],
  currentSportsbook: string | null,
): boolean {
  return !isLegBlocked(newLeg, existingLegs, currentSportsbook).canAdd;
}

/**
 * Check if two specific legs are correlated (would block each other)
 */
export function areLegsCorrelated(leg1: CorrelationLeg, leg2: CorrelationLeg): boolean {
  return !isLegBlocked(leg2, [leg1], null).canAdd;
}
